import { ICellRendererParams } from "ag-grid-community";
import { Flex, Image } from "@chakra-ui/react";
import { FlatProduct } from "@/features/line-sheet-sets/helpers/sheet-state";
import RepresentativeProductImage, {
  MAX_PRODUCT_IMAGE_COUNT,
} from "@/features/order-sheet-sets/representative-product-image";
import ProductImage from "@/features/order-sheet-sets/product-image";
import useDisclosureWithData from "@/features/order-sheet-sets/hooks/use-disclosure-with-data";
import ModifyImageModal, { ModifyImageModalProps } from "@/features/order-sheet-sets/modify-image-modal";
import useIdentity from "@/features/ui/hooks/use-identity";
import CustomIcon from "../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

interface ProductImageCellProps
  extends ICellRendererParams<FlatProduct, FlatProduct> {
  isCollapsed: boolean;
  isLineSheet: boolean;
  mutate: () => void;
  // 주문서(바이어)에서만 넘어온다 — 없으면 별을 그리지 않는다.
  likeControl?: {
    isLiked: (productId: number) => boolean;
    onToggle: (productId: number) => void;
  };
}

export const PRODUCT_IMAGE_SIZE = 74;

export default function ProductImageCell({
  rowIndex,
  value,
  node,
  isCollapsed,
  isLineSheet,
  mutate,
  likeControl,
  api,
}: ProductImageCellProps) {
  const identity = useIdentity();
  const { tTitle } = useI18nHelper();
  const isAgency = identity?.company?.type === "AGENCY";

  const {
    isOpen: isModifyImageModalOpen,
    onClose: onModifyImageModalClose,
    data: ModifyImageModalData,
    openWithData: openModifyImageModalWithData,
  } = useDisclosureWithData<ModifyImageModalProps>();

  if (node.data?.id! < 0) {
    return (
      <Flex
        dangerouslySetInnerHTML={{ __html: node.data?.brandSKUEtc || "" }}
      />
    )

  }

  if (rowIndex % 3 === 0) {
    return (
      <Flex
        position={"relative"}
        boxSize={`${PRODUCT_IMAGE_SIZE}px`}
        gap={"4px"}
        zIndex={"3"}
        alignItems={"center"}
        width={"100%"}
      >
        {/* 우상단 별 — 카드 보기와 같은 관심 표시. 이미지를 조금 가려도 눈에 띄는 게 낫다. */}
        {likeControl && value && (
          <Flex
            as={"button"}
            position={"absolute"}
            top={"0"}
            right={"0"}
            zIndex={"11"}
            alignItems={"center"}
            justifyContent={"center"}
            boxSize={"24px"}
            aria-label={tTitle("favorite")}
            title={tTitle("favorite")}
            onClick={(event) => {
              event.stopPropagation();
              likeControl.onToggle(value.id);
            }}
          >
            <CustomIcon
              src={likeControl.isLiked(value.id) ? "star-solid" : "star"}
              width={20}
              height={20}
              color={likeControl.isLiked(value.id) ? "#1272EF" : "#8F8F8C"}
            />
          </Flex>
        )}
        {isModifyImageModalOpen &&
          ModifyImageModalData && (
            <ModifyImageModal
              productId={value.id}
              searchWord={value.searchWord || ""}
              sku={value.sku || ""}
              brandName={value.brandName || ""}
              color={value.color || ""}
              isOpen={isModifyImageModalOpen}
              onClose={onModifyImageModalClose}
              mutate={mutate}
            />
          )}
        {value && value.imageUrls && value.imageUrls.length > 0 && isCollapsed && <RepresentativeProductImage value={value.imageUrls} />}
        {isAgency && !isCollapsed && <Image
          borderRadius={"8px"}
          background={"#fff"}
          alt={"alt"}
          src={window.location.origin + "/img_add.png"}
          boxSize={`${PRODUCT_IMAGE_SIZE}px`}
          objectFit={"contain"}
          zIndex={"10"}
          onClick={() => {
            openModifyImageModalWithData(() => {
              return {
                productId: value.id,
                searchWord: value.searchWord || "",
                sku: value.sku || "",
                brandName: value.brandName || "",
                color: value.color || "",
                imageUrls: value.imageUrls,
                isOpen: true,
                isClosed: false,
                mutate: mutate,
              };
            });
          }}
        />}
        {value && value.imageUrls &&
          !isCollapsed &&
          value.imageUrls.slice(0, MAX_PRODUCT_IMAGE_COUNT).map((item, index) => {
            return <ProductImage key={index} value={item} mutate={mutate} isLineSheet={isLineSheet} productId={value.id} aiImageChecked={value.aiImageChecked || false}/>;
          })}
      </Flex>
    );
  }
  return null;
}
